/**
 * Seed Export Configuration
 *
 * ANALYSIS FINDINGS:
 * - Original component: ExportSeeds.tsx (212 lines)
 * - Filters: status (from useGetSeedFilters), class (TLD, Domain, CIDR, IPv4, IPv6)
 * - Columns: 10 exportable fields including seed-specific fields
 * - Storage key: ADVANCE_EXPORT_OPTIONS.seeds.v3
 * - Query: Simple Seed node with status and class filters
 *
 * MIGRATION NOTES:
 * - Seeds have different status values than Assets/Risks (A/F/D/R/etc)
 * - Class filter represents seed type (domain, tld, ip, cidr, etc)
 * - Email filter only applies to domain seeds
 * - Country filter only applies to IP seeds
 */

import {
  CheckCircleIcon,
  ClockIcon,
  TrashIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import React from 'react';

import { Frozen as FrozenIcon } from '@/components/chariot-ui';
import type {
  ExportColumnConfig,
  UniversalExportConfig,
} from '@/components/export/universal/types';
import { GraphQueryBuilder } from '@/components/export/universal/utils';
import type { StatusChipVariant } from '@/components/legacy-ui/StatusChip';
import type { Seed } from '@/types';
import { SeedStatus, SeedStatusLabel } from '@/types';

/**
 * Seed class/type options
 * Matches the type filter in original ExportSeeds component
 */
const SEED_CLASS_OPTIONS = [
  { label: 'TLD', value: 'tld' },
  { label: 'Domain', value: 'domain' },
  { label: 'CIDR', value: 'cidr' },
  { label: 'IPv4', value: 'ipv4' },
  { label: 'IPv6', value: 'ipv6' },
] as const;

/**
 * Seed status styling map (matches SeedStatus.tsx patterns)
 */
const SEED_STATUS_STYLE: Record<
  string,
  { variant: StatusChipVariant; icon: React.ReactNode }
> = {
  [SeedStatus.Accept]: {
    variant: 'info',
    icon: React.createElement(CheckCircleIcon, {
      className: 'size-3 shrink-0',
    }),
  },
  [SeedStatus.ActivePassive]: {
    variant: 'info',
    icon: React.createElement(CheckCircleIcon, {
      className: 'size-3 shrink-0',
    }),
  },
  [SeedStatus.Pending]: {
    variant: 'high',
    icon: React.createElement(ClockIcon, { className: 'size-3 shrink-0' }),
  },
  [SeedStatus.Reject]: {
    variant: 'critical',
    icon: React.createElement(XCircleIcon, { className: 'size-3 shrink-0' }),
  },
  [SeedStatus.Freeze]: {
    variant: 'muted',
    icon: React.createElement(FrozenIcon, { className: 'size-3 shrink-0' }),
  },
  [SeedStatus.Delete]: {
    variant: 'muted',
    icon: React.createElement(TrashIcon, { className: 'size-3 shrink-0' }),
  },
};

/**
 * Seed status options
 * Seeds have unique status values different from Assets/Risks
 */
const SEED_STATUS_OPTIONS = Object.values(SeedStatus)
  .filter(
    // Exclude low-priority statuses per useGetSeedFilters
    value => value !== SeedStatus.PendingLow && value !== SeedStatus.AcceptLow
  )
  .map(value => ({
    label: SeedStatusLabel[value],
    value: value,
    ...SEED_STATUS_STYLE[value],
  }));

/**
 * Seed column configuration - 13 columns total
 * Maps to original ColumnOptions in ExportSeeds.tsx
 */
export const SEED_COLUMNS: readonly ExportColumnConfig[] = [
  { id: 'asset_identifier', label: 'Identifier', defaultSelected: true },
  { id: 'asset_name', label: 'Name', defaultSelected: true },
  { id: 'status', label: 'Status', defaultSelected: true },
  { id: 'origins', label: 'Origins', defaultSelected: true },
  { id: 'tags', label: 'Tags', defaultSelected: true },
  { id: 'registrar', label: 'Registrar', defaultSelected: true },
  { id: 'registrant', label: 'Registrant', defaultSelected: true },
  { id: 'country', label: 'Country', defaultSelected: true },
  { id: 'email', label: 'Email', defaultSelected: true },
  { id: 'class', label: 'Class', defaultSelected: true },
  { id: 'history', label: 'History', defaultSelected: true },
  { id: 'created', label: 'Created', defaultSelected: true },
  { id: 'visited', label: 'Visited', defaultSelected: true },
] as const;

/**
 * Universal export configuration for Seeds
 *
 * FEATURES:
 * - Status filter with seed-specific statuses
 * - Class filter for seed types (domain, ip, cidr, etc)
 * - 13 exportable columns
 * - Conditional filters shown only when exporting all (not selected rows)
 */
export const seedExportConfig: UniversalExportConfig<Seed> = {
  entityType: 'Seed',
  apiEntityType: 'seed',
  storageKey: 'ADVANCE_EXPORT_OPTIONS.seeds.v5',

  filterSections: [
    {
      id: 'status',
      label: 'Status',
      options: SEED_STATUS_OPTIONS,
      defaultSelected: SEED_STATUS_OPTIONS, // Default: all statuses
      showWhenSelected: false, // Hide when rows are selected
    },
    {
      id: 'class',
      label: 'Class',
      options: SEED_CLASS_OPTIONS,
      defaultSelected: SEED_CLASS_OPTIONS, // Default: all classes
      showWhenSelected: false, // Hide when rows are selected
    },
  ],

  columns: SEED_COLUMNS,

  formats: [
    { label: 'CSV', value: 'csv' },
    { label: 'JSON', value: 'json' },
    { label: 'PDF', value: 'pdf' },
  ],
  defaultFormat: { label: 'CSV', value: 'csv' },

  /**
   * Build graph query for seed export
   *
   * QUERY STRUCTURE:
   * - Labels: ['Seed']
   * - Filters: status, class (when selected)
   * - OrderBy: dns (ascending)
   *
   * BEHAVIOR:
   * - When selectedRows is empty: Apply all filters
   * - When selectedRows has values: Only export selected seeds
   */
  buildQuery: (filters, _selectedRows) => {
    let builder = GraphQueryBuilder.create(['Seed']);

    // Add status filter if selected
    if (filters.status?.length > 0) {
      builder = builder.filter(
        'status',
        '=',
        filters.status.map(s => s.value)
      );
    }

    // Add class filter if selected
    if (filters.class?.length > 0) {
      builder = builder.filter(
        'class',
        '=',
        filters.class.map(c => c.value)
      );
    }

    // Set ordering (matches original: orderBy: 'dns', descending: false)
    return builder.orderBy('dns', false).build();
  },

  /**
   * Validate export selections
   *
   * VALIDATION RULES:
   * - At least one column must be selected
   * - If no rows selected, at least one filter must be selected
   */
  validateExport: (filters, columns, selectedRows) => {
    const errors = [];

    // Validate columns
    if (columns.length === 0) {
      errors.push({
        field: 'columns' as const,
        message: 'Please select at least one column to export.',
      });
    }

    // Validate filters when exporting all
    if (selectedRows.length === 0) {
      const hasStatusFilter = filters.status && filters.status.length > 0;
      const hasClassFilter = filters.class && filters.class.length > 0;

      if (!hasStatusFilter && !hasClassFilter) {
        errors.push({
          field: 'filters' as const,
          message:
            'Please select at least one status or class filter when exporting all seeds.',
        });
      }
    }

    return errors.length > 0 ? errors : null;
  },
};
